import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { themes } from './theme';

const INDIA_PRICE_LABEL = process.env.REACT_APP_INDIA_PRICE_LABEL || 'Rs 149';
const INTERNATIONAL_PRICE_LABEL = process.env.REACT_APP_INTERNATIONAL_PRICE_LABEL || '$5';
const LIFETIME_PRICE = process.env.REACT_APP_PREMIUM_PRICE_LABEL || `${INDIA_PRICE_LABEL} / ${INTERNATIONAL_PRICE_LABEL}`;
const SUPPORT_EMAIL = process.env.REACT_APP_PAYMENT_SUPPORT_EMAIL || 'kapoorashish213@gmail.com';

function decodeSharedReportFromUrl() {
  try {
    const encoded = new URLSearchParams(window.location.search).get('report');
    if (!encoded) return '';
    const binary = atob(encoded);
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return '';
  }
}

export default function PinLogin({ theme = themes.light, themeMode = 'light', onToggleTheme }) {
  const isMobile = useIsMobile();
  const [sharedReport] = useState(decodeSharedReportFromUrl);
  const [mode, setMode] = useState('signin');
  const [zoomImage, setZoomImage] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');

    const credentials = { email: email.trim(), password };
    if (mode === 'forgot') {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(credentials.email, {
        redirectTo: window.location.origin,
      });
      setBusy(false);
      if (resetError) {
        setError(resetError.message);
      } else {
        setMode('signin');
        setMessage('Password reset email sent. Check your inbox for the reset link.');
      }
      return;
    }

    if (mode === 'signup' && password !== confirmPassword) {
      setBusy(false);
      setError('Passwords do not match.');
      return;
    }

    const { error: authError } = mode === 'signin'
      ? await supabase.auth.signInWithPassword(credentials)
      : await supabase.auth.signUp({
          ...credentials,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              name: name.trim(),
            },
          },
        });

    if (authError) {
      setError(authError.message);
    } else if (mode === 'signup') {
      await supabase.auth.signOut();
      setPassword('');
      setConfirmPassword('');
      setMode('signin');
      setMessage('Account created. Check your email and confirm the link before signing in.');
    }

    setBusy(false);
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      backgroundImage: theme.bgImage,
      fontFamily: "'Rajdhani', sans-serif",
      color: theme.text,
      padding: isMobile ? 10 : 18,
      boxSizing: 'border-box',
    }}>
      <div style={{
        maxWidth: 1180,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.25fr) minmax(320px, 380px)',
        gap: isMobile ? 10 : 20,
        alignItems: 'start',
      }}>
        <main>
          {sharedReport && (
            <section style={sectionStyle(theme)}>
              <h2 style={headingStyle(theme)}>Shared Weekly Report</h2>
              <p style={textStyle(theme)}>
                This is a read-only snapshot shared from Weekly Planner. It does not give access to the owner account or planner database.
              </p>
              <pre style={{
                margin: '14px 0 0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: theme.text,
                background: theme.taskSurface || theme.surfaceSoft,
                border: `1px solid ${theme.borderSoft}`,
                borderRadius: 8,
                padding: 14,
                lineHeight: 1.45,
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: 14,
              }}>
                {sharedReport}
              </pre>
            </section>
          )}

          <section style={heroSection(theme, isMobile)}>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={eyebrow(theme)}>Built for timesheets, not endless project management</div>
              <h1 style={heroTitle(theme, isMobile)}>
                Stop rebuilding your week every Friday.
              </h1>
              <p style={heroCopy(theme, isMobile)}>
                Capture tasks, hours, notes, and subtasks while you work. When timesheet day arrives, export or share a clean weekly report in minutes.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
                <button type="button" onClick={() => setMode('signup')} style={heroButton(theme)}>
                  Start free - no card
                </button>
                <button type="button" onClick={() => setZoomImage('/planner-preview.png')} style={smallButton(theme)}>
                  View product
                </button>
                <button type="button" onClick={onToggleTheme} style={smallButton(theme)}>
                  {themeMode === 'light' ? 'Dark mode' : 'Light mode'}
                </button>
              </div>
              <div style={metricStrip(isMobile)}>
                <MetricPill theme={theme} value="5 min" label="Friday reporting" />
                <MetricPill theme={theme} value="Excel" label="Premium export" />
                <MetricPill theme={theme} value="Rs 149 / $5" label="Lifetime premium" />
              </div>
            </div>
            <HeroReelCard theme={theme} />
          </section>

          <section style={sectionStyle(theme)}>
            <div style={sectionHeaderRow(isMobile)}>
              <div>
                <div style={eyebrow(theme)}>Product walkthrough</div>
                <h2 style={headingStyle(theme)}>See the weekly flow before signing up</h2>
              </div>
              <button type="button" onClick={() => setMode('signup')} style={smallButton(theme)}>
                Try it free
              </button>
            </div>
            <ScreenshotGallery theme={theme} isMobile={isMobile} onOpen={setZoomImage} />
          </section>

          <section style={sectionStyle(theme)}>
            <div style={sectionHeaderRow(isMobile)}>
              <div>
                <div style={eyebrow(theme)}>Why it converts</div>
                <h2 style={headingStyle(theme)}>A planner that ends in an output</h2>
              </div>
            </div>
            <div style={bentoGrid(isMobile)}>
              {[
                ['Daily capture', 'Write tasks as they happen, with hours and notes while the details are fresh.', '01'],
                ['Tagged sections', 'Separate Office, Client, Study, Personal, or House work inside each day.', '02'],
                ['Subtasks + notes', 'Keep context under the task instead of hunting through chat history later.', '03'],
                ['Shareable report', 'Send a read-only weekly snapshot to a manager or client without exposing the account.', '04'],
                ['Premium Excel', 'Export selected weeks from the backend after entitlement verification.', '05'],
                ['Mobile-first mode', 'Phone users get a focused single-day view instead of a tiny desktop board.', '06'],
              ].map(([title, text, number]) => (
                <FeatureTile key={title} theme={theme} title={title} text={text} number={number} />
              ))}
            </div>
          </section>

          <section style={sectionStyle(theme)}>
            <div style={sectionHeaderRow(isMobile)}>
              <div>
                <div style={eyebrow(theme)}>For the people who report hours</div>
                <h2 style={headingStyle(theme)}>Built around real weekly pain</h2>
              </div>
            </div>
            <div style={audienceGrid(isMobile)}>
              <TestimonialCard
                theme={theme}
                quote="Friday reporting is the annoying part. This keeps the proof of work ready before Friday starts."
                role="Corporate employee"
              />
              <TestimonialCard
                theme={theme}
                quote="Client updates need hours, notes, and unfinished follow-ups. A plain to-do list never gives that context."
                role="Freelancer"
              />
              <TestimonialCard
                theme={theme}
                quote="I need to see where study time actually went, not just whether I checked a box."
                role="Student"
              />
            </div>
            <p style={{ ...textStyle(theme), marginTop: 12 }}>
              These role-based proof cards should be replaced with named real testimonials as soon as you collect them.
            </p>
          </section>

          <section style={sectionStyle(theme)}>
            <div style={sectionHeaderRow(isMobile)}>
              <div>
                <div style={eyebrow(theme)}>Pricing that is easy to say yes to</div>
                <h2 style={headingStyle(theme)}>Start free. Upgrade only for premium output.</h2>
              </div>
            </div>
            <div style={pricingGrid(isMobile)}>
              <PriceCard
                theme={theme}
                title="Free"
                price="Rs 0"
                note="Start tracking today"
                features={['Tasks, tags, notes, subtasks, hours', 'Templates and mobile view', 'Copy report and share snapshot']}
                action="Create free account"
                onClick={() => setMode('signup')}
              />
              <PriceCard
                theme={theme}
                title="Premium India"
                price={INDIA_PRICE_LABEL}
                note="Lifetime access"
                featured
                features={['No ads', 'Backend-secured Excel export', 'Selected week export history']}
                action="Unlock after signup"
                onClick={() => setMode('signup')}
              />
              <PriceCard
                theme={theme}
                title="Premium Global"
                price={INTERNATIONAL_PRICE_LABEL}
                note="Lifetime access"
                features={['No ads', 'Excel export', 'Works for freelancers and students globally']}
                action="Start free first"
                onClick={() => setMode('signup')}
              />
            </div>
          </section>

          <section style={sectionStyle(theme)}>
            <div style={sectionHeaderRow(isMobile)}>
              <div>
                <div style={eyebrow(theme)}>Trust layer</div>
                <h2 style={headingStyle(theme)}>Secure enough for work notes</h2>
              </div>
            </div>
            <div style={policyGrid}>
              {[
                'Supabase authentication with email verification and password reset',
                'Row-level security so each user can access only their own planner data',
                'Premium Excel export is verified by a backend Edge Function',
                'Read-only report links are snapshots and do not open account access',
                'Secure payment processing for India and international buyers',
                'Support, privacy, refund, and deletion details are visible before signup',
              ].map(item => <InfoLine key={item} text={item} theme={theme} />)}
            </div>
          </section>

          <section style={sectionStyle(theme)}>
            <div style={sectionHeaderRow(isMobile)}>
              <div>
                <div style={eyebrow(theme)}>Against generic tools</div>
                <h2 style={headingStyle(theme)}>Less setup than Notion. More timesheet output than a to-do app.</h2>
              </div>
            </div>
            <div style={policyGrid}>
              {[
                ['vs Notion', 'Purpose-built for weekly timesheet records instead of a blank workspace.'],
                ['vs Clockify', 'Lightweight task context and weekly export without complex reporting setup.'],
                ['vs Spreadsheets', 'Mobile-friendly daily capture with tags, subtasks, notes, history, and export.'],
              ].map(([title, text]) => (
                <div key={title} style={featureCard(theme)}>
                  <strong style={{ color: theme.primary, fontFamily: "'Orbitron', sans-serif" }}>{title}</strong>
                  <span style={{ color: theme.muted, display: 'block', marginTop: 7, lineHeight: 1.45 }}>{text}</span>
                </div>
              ))}
            </div>
          </section>

          <section style={sectionStyle(theme)}>
            <h2 style={headingStyle(theme)}>FAQ</h2>
            <div style={policyGrid}>
              {[
                ['Can I use it for free?', 'Yes. The free plan is supported by ads and lets you track your weekly work before upgrading.'],
                ['What does Premium unlock?', 'Premium removes ads and unlocks formatted Excel export for manually selected weeks.'],
                ['Can my manager view my report?', 'Yes. Use Share Link for a read-only weekly snapshot, or Copy Report for email and chat.'],
                ['Will my data be private?', 'Each account is protected with Supabase authentication and row-level security.'],
                ['What happens if I forget my password?', 'Use Forgot password on the login form to receive a reset link by email.'],
                ['Can I delete my data?', `Yes. Email ${SUPPORT_EMAIL} for account, billing, privacy, or data deletion support.`],
              ].map(([title, text]) => (
                <div key={title} style={featureCard(theme)}>
                  <strong style={{ color: theme.primary, fontFamily: "'Orbitron', sans-serif" }}>{title}</strong>
                  <span style={{ color: theme.muted, display: 'block', marginTop: 7, lineHeight: 1.45 }}>{text}</span>
                </div>
              ))}
            </div>
          </section>

          <section style={sectionStyle(theme)}>
            <h2 style={headingStyle(theme)}>Refund And Cancellation Policy</h2>
            <p style={textStyle(theme)}>
              This is a digital premium-access product. Users can use the free ad-supported planner before paying. Because premium access is delivered instantly after payment, refunds are generally not provided after successful purchase except for duplicate payments, payment errors, or access issues that cannot be resolved.
            </p>
            <p style={textStyle(theme)}>
              For billing or access support, contact {SUPPORT_EMAIL}.
            </p>
          </section>

          <section style={sectionStyle(theme)}>
            <h2 style={headingStyle(theme)}>Privacy Policy</h2>
            <p style={textStyle(theme)}>
              The planner stores account email, task data, subtasks, notes, tags, weekly history, and payment entitlement status. This data is used only to provide the planner service and is not sold. User planner data is protected with Supabase authentication and row-level security.
            </p>
            <p style={textStyle(theme)}>
              Users can request account deletion, data deletion, or privacy support by emailing {SUPPORT_EMAIL}. Account-related emails are used for login, verification, password reset, payment access, and support.
            </p>
          </section>

          <section style={sectionStyle(theme)}>
            <h2 style={headingStyle(theme)}>Terms And Contact</h2>
            <p style={textStyle(theme)}>
              Users are responsible for the task and timesheet information they enter. The planner is provided as a productivity tool and does not guarantee employer approval of any timesheet or work record.
            </p>
            <p style={textStyle(theme)}>
              Support and grievance contact: {SUPPORT_EMAIL}. Typical response time is within 3 business days.
            </p>
          </section>
        </main>

        <aside style={{ position: isMobile ? 'static' : 'sticky', top: 18, order: isMobile ? -1 : 0 }}>
          <form onSubmit={submit} style={{
            width: '100%',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 10,
            boxShadow: theme.shadowStrong,
            padding: isMobile ? 18 : 24,
            boxSizing: 'border-box',
          }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: theme.primary, fontFamily: "'Orbitron', sans-serif" }}>
              {mode === 'signin' ? 'Login' : mode === 'forgot' ? 'Reset Password' : 'New User Register'}
            </h2>
            <p style={{ margin: '6px 0 22px', color: theme.muted, fontSize: 14 }}>
              {mode === 'signin'
                ? 'Existing users can open their planner here.'
                : mode === 'forgot'
                  ? 'Enter your registered email to receive a reset link.'
                  : 'Create your planner account. Email confirmation is required.'}
            </p>

            {mode === 'signup' && (
              <label style={labelStyle(theme)}>
                Name
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  autoComplete="name"
                  style={inputStyle(theme)}
                />
              </label>
            )}

            <label style={labelStyle(theme)}>
              Email
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                style={inputStyle(theme)}
              />
            </label>

            {mode !== 'forgot' && (
              <label style={labelStyle(theme)}>
                {mode === 'signin' ? 'Password' : 'Create password'}
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  style={inputStyle(theme)}
                />
              </label>
            )}

            {mode === 'signup' && (
              <label style={labelStyle(theme)}>
                Re-enter password
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  style={inputStyle(theme)}
                />
              </label>
            )}

            {error && <div style={errorStyle(theme)}>{error}</div>}
            {message && <div style={messageStyle(theme)}>{message}</div>}

            <button type="submit" disabled={busy} style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 8,
              border: `1px solid ${theme.primary}`,
              background: busy ? theme.primarySoft : `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
              color: '#ffffff',
              cursor: busy ? 'wait' : 'pointer',
              fontWeight: 800,
              fontSize: 14,
              marginTop: 8,
            }}>
              {busy ? 'Loading...' : mode === 'signin' ? 'Sign in' : mode === 'forgot' ? 'Send reset link' : 'Create account'}
            </button>

            {mode === 'signin' && (
              <button
                type="button"
                onClick={() => {
                  setMode('forgot');
                  setError('');
                  setMessage('');
                  setPassword('');
                  setConfirmPassword('');
                }}
                style={{
                  width: '100%',
                  marginTop: 10,
                  background: 'transparent',
                  border: 'none',
                  color: theme.accent,
                  cursor: 'pointer',
                  fontWeight: 800,
                }}
              >
                Forgot password?
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setMode(mode === 'signin' ? 'signup' : 'signin');
                setError('');
                setMessage('');
                setPassword('');
                setConfirmPassword('');
              }}
              style={{
                width: '100%',
                marginTop: 12,
                background: 'transparent',
                border: 'none',
                color: theme.primary,
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              {mode === 'signin' ? 'New user? Register here' : 'Already registered? Login here'}
            </button>
          </form>
        </aside>
      </div>
      {zoomImage && (
        <ImageZoom
          src={zoomImage}
          theme={theme}
          onClose={() => setZoomImage(null)}
        />
      )}
    </div>
  );
}

function PreviewImage({ src, label, theme, onOpen }) {
  return (
    <figure style={{
      margin: 0,
      border: `1px solid ${theme.borderSoft}`,
      borderRadius: 10,
      overflow: 'hidden',
      background: theme.surfaceSoft,
      boxShadow: theme.shadow,
      marginTop: 14,
    }}>
      <button type="button" onClick={onOpen} style={{
        display: 'block',
        width: '100%',
        padding: 0,
        border: 0,
        background: theme.mode === 'dark' ? '#0d1021' : '#f7fbff',
        cursor: 'zoom-in',
      }}>
        <img
          src={src}
          alt={label}
          style={{
            display: 'block',
            width: '100%',
            aspectRatio: '1200 / 680',
            objectFit: 'contain',
          }}
        />
      </button>
      <figcaption style={{
        padding: '9px 11px',
        color: theme.muted,
        fontSize: 13,
        fontWeight: 800,
      }}>
        {label}
      </figcaption>
    </figure>
  );
}

function HeroReelCard({ theme }) {
  return (
    <div style={heroShot(theme)}>
      <video
        src="/weekly-planner-feature-ad-reel.mp4"
        poster="/weekly-planner-feature-ad-cover.png"
        autoPlay
        controls
        playsInline
        muted
        loop
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center center',
          borderRadius: 14,
        }}
      />
      <div style={{
        position: 'absolute',
        left: 16,
        right: 16,
        bottom: 16,
        border: `1px solid ${theme.borderSoft}`,
        borderRadius: 12,
        background: theme.mode === 'dark' ? 'rgba(13,16,33,0.86)' : 'rgba(255,255,255,0.86)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        padding: 12,
        color: theme.text,
        textAlign: 'left',
        boxShadow: theme.shadow,
      }}>
        <div style={{ color: theme.accent, fontWeight: 900, fontFamily: "'Orbitron', sans-serif", fontSize: 12 }}>
          WATCH THE PRODUCT FLOW
        </div>
        <div style={{ fontWeight: 900, marginTop: 4 }}>
          Works smoothly on mobile and desktop, so you can log work anywhere and finish reports later.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {['Mobile ready', 'Desktop workspace', 'Excel export'].map(label => (
            <span key={label} style={{
              color: theme.text,
              background: theme.surfaceSoft,
              border: `1px solid ${theme.borderSoft}`,
              borderRadius: 999,
              padding: '4px 8px',
              fontSize: 12,
              fontWeight: 900,
            }}>
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScreenshotGallery({ theme, isMobile, onOpen }) {
  const shots = [
    ['Weekly board', 'See tagged work, hours, notes, and progress in one view.', '/planner-preview.png'],
    ['Mobile workflow', 'Single-day layout keeps phone use clean and focused.', '/planner-preview.png'],
    ['Premium output', 'Export selected weeks into a formatted timesheet file.', '/planner-preview.png'],
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1.35fr 1fr',
      gap: 12,
      marginTop: 14,
    }}>
      <button type="button" onClick={() => onOpen('/planner-preview.png')} style={galleryMain(theme)}>
        <img src="/planner-preview.png" alt="Weekly Planner desktop board" style={{ width: '100%', display: 'block', borderRadius: 10 }} />
      </button>
      <div style={{ display: 'grid', gap: 10 }}>
        {shots.map(([title, text, src]) => (
          <button key={title} type="button" onClick={() => onOpen(src)} style={galleryTile(theme)}>
            <strong style={{ color: theme.primary, fontFamily: "'Orbitron', sans-serif" }}>{title}</strong>
            <span style={{ color: theme.muted, display: 'block', marginTop: 4, lineHeight: 1.35 }}>{text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MetricPill({ theme, value, label }) {
  return (
    <div style={{
      border: `1px solid ${theme.borderSoft}`,
      background: theme.surfaceSoft,
      borderRadius: 10,
      padding: '10px 12px',
      minWidth: 120,
    }}>
      <div style={{ color: theme.primary, fontFamily: "'Orbitron', sans-serif", fontWeight: 900 }}>{value}</div>
      <div style={{ color: theme.muted, fontSize: 12, fontWeight: 800 }}>{label}</div>
    </div>
  );
}

function FeatureTile({ theme, title, text, number }) {
  return (
    <div style={{
      border: `1px solid ${theme.borderSoft}`,
      borderRadius: 12,
      padding: 16,
      minHeight: 150,
      background: theme.taskSurface || theme.surfaceSoft,
      boxShadow: theme.shadow,
    }}>
      <div style={{ color: theme.faint, fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 13 }}>{number}</div>
      <h3 style={{ margin: '12px 0 7px', color: theme.text, fontSize: 19, fontWeight: 900 }}>{title}</h3>
      <p style={{ margin: 0, color: theme.muted, lineHeight: 1.45 }}>{text}</p>
    </div>
  );
}

function TestimonialCard({ theme, quote, role }) {
  return (
    <div style={{
      border: `1px solid ${theme.borderSoft}`,
      background: theme.taskSurface || theme.surfaceSoft,
      borderRadius: 12,
      padding: 16,
      boxShadow: theme.shadow,
    }}>
      <div style={{ color: theme.accent, fontSize: 24, lineHeight: 1 }}>“</div>
      <p style={{ margin: '4px 0 14px', color: theme.text, lineHeight: 1.45, fontWeight: 800 }}>{quote}</p>
      <div style={{ color: theme.muted, fontWeight: 900 }}>{role}</div>
    </div>
  );
}

function PriceCard({ theme, title, price, note, features, action, onClick, featured = false }) {
  return (
    <div style={{
      border: `1px solid ${featured ? theme.primary : theme.borderSoft}`,
      borderRadius: 14,
      padding: 18,
      background: featured ? `linear-gradient(135deg, ${theme.primarySoft}, ${theme.surfaceSoft})` : theme.taskSurface,
      boxShadow: featured ? theme.shadowStrong : theme.shadow,
      position: 'relative',
    }}>
      {featured && (
        <div style={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: '#fff',
          background: theme.primary,
          borderRadius: 999,
          padding: '4px 8px',
          fontSize: 11,
          fontWeight: 900,
        }}>
          Most useful
        </div>
      )}
      <h3 style={{ margin: 0, color: theme.text, fontSize: 20, fontWeight: 900 }}>{title}</h3>
      <div style={{ marginTop: 10, color: theme.primary, fontFamily: "'Orbitron', sans-serif", fontSize: 28, fontWeight: 900, wordBreak: 'normal' }}>{price}</div>
      <div style={{ color: theme.muted, fontWeight: 800, marginBottom: 14 }}>{note}</div>
      {features.map(feature => (
        <div key={feature} style={{ display: 'flex', gap: 8, color: theme.text, margin: '8px 0', fontWeight: 800 }}>
          <span style={{ color: theme.success }}>OK</span>
          <span>{feature}</span>
        </div>
      ))}
      <button type="button" onClick={onClick} style={{ ...heroButton(theme), width: '100%', marginTop: 14 }}>
        {action}
      </button>
    </div>
  );
}

function ImageZoom({ src, theme, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: theme.mode === 'dark' ? 'rgba(2,6,23,0.88)' : 'rgba(15,23,42,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        cursor: 'zoom-out',
      }}
    >
      <button type="button" onClick={onClose} style={{
        position: 'absolute',
        top: 18,
        right: 18,
        border: `1px solid ${theme.border}`,
        background: theme.surface,
        color: theme.text,
        borderRadius: 8,
        padding: '8px 12px',
        cursor: 'pointer',
        fontWeight: 900,
      }}>
        Close
      </button>
      <img
        src={src}
        alt="Weekly Planner zoomed preview"
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '96vw',
          maxHeight: '90vh',
          objectFit: 'contain',
          borderRadius: 10,
          boxShadow: '0 30px 100px rgba(0,0,0,0.45)',
          cursor: 'default',
        }}
      />
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 760 : false
  ));

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 760);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return isMobile;
}

function InfoLine({ text, theme }) {
  return (
    <div style={{
      padding: '9px 10px',
      border: `1px solid ${theme.borderSoft}`,
      borderRadius: 8,
      background: theme.surfaceSoft,
      color: theme.text,
      fontWeight: 700,
    }}>
      {text}
    </div>
  );
}

function sectionStyle(theme) {
  return {
    background: theme.glassStrong,
    backdropFilter: theme.glassBlur,
    WebkitBackdropFilter: theme.glassBlur,
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 10,
    boxShadow: theme.shadow,
    padding: 22,
    marginBottom: 14,
  };
}

function featureCard(theme) {
  return {
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 8,
    background: theme.taskSurface || theme.surfaceSoft,
    padding: 13,
  };
}

const policyGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 9,
};

function headingStyle(theme) {
  return {
    margin: '0 0 10px',
    color: theme.primary,
    fontSize: 20,
    fontFamily: "'Orbitron', sans-serif",
    fontWeight: 900,
  };
}

function textStyle(theme) {
  return {
    margin: '8px 0 0',
    color: theme.muted,
    fontSize: 14,
    lineHeight: 1.55,
  };
}

function smallButton(theme) {
  return {
    border: `1px solid ${theme.border}`,
    background: theme.surfaceSoft,
    color: theme.text,
    borderRadius: 8,
    padding: '7px 10px',
    cursor: 'pointer',
    fontWeight: 800,
    fontFamily: "'Orbitron', sans-serif",
  };
}

function heroButton(theme) {
  return {
    border: `1px solid ${theme.primary}`,
    background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
    color: '#ffffff',
    borderRadius: 8,
    padding: '9px 14px',
    cursor: 'pointer',
    fontWeight: 900,
    fontFamily: "'Orbitron', sans-serif",
    boxShadow: theme.shadow,
  };
}

function audienceGrid(isMobile) {
  return {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
    gap: 10,
  };
}

function heroSection(theme, isMobile) {
  return {
    position: 'relative',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 0.95fr) minmax(360px, 1.05fr)',
    gap: isMobile ? 18 : 24,
    alignItems: 'center',
    background: theme.glassStrong,
    backdropFilter: theme.glassBlur,
    WebkitBackdropFilter: theme.glassBlur,
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 18,
    boxShadow: theme.shadowStrong,
    padding: isMobile ? 22 : 28,
    marginBottom: 14,
  };
}

function heroTitle(theme, isMobile) {
  return {
    margin: '10px 0 0',
    color: theme.text,
    fontFamily: "'Orbitron', sans-serif",
    fontSize: isMobile ? 38 : 62,
    lineHeight: 1.03,
    letterSpacing: 0,
    fontWeight: 900,
  };
}

function heroCopy(theme, isMobile) {
  return {
    margin: '14px 0 0',
    color: theme.muted,
    fontSize: isMobile ? 17 : 20,
    lineHeight: 1.5,
    maxWidth: 720,
  };
}

function eyebrow(theme) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: theme.accent,
    background: theme.accentSoft,
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    fontFamily: "'Orbitron', sans-serif",
    fontWeight: 900,
  };
}

function metricStrip(isMobile) {
  return {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
    gap: 10,
    marginTop: 22,
  };
}

function heroShot(theme) {
  return {
    position: 'relative',
    display: 'block',
    width: '100%',
    minHeight: 390,
    alignSelf: 'start',
    marginTop: -8,
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 18,
    padding: 8,
    background: theme.surfaceSoft,
    boxShadow: theme.shadowStrong,
    overflow: 'hidden',
  };
}

function sectionHeaderRow(isMobile) {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: isMobile ? 'wrap' : 'nowrap',
  };
}

function bentoGrid(isMobile) {
  return {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    marginTop: 14,
  };
}

function pricingGrid(isMobile) {
  return {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(220px, 1fr))',
    gap: 12,
    marginTop: 14,
    alignItems: 'stretch',
  };
}

function galleryMain(theme) {
  return {
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 14,
    padding: 8,
    background: theme.surfaceSoft,
    boxShadow: theme.shadow,
    cursor: 'zoom-in',
    overflow: 'hidden',
  };
}

function galleryTile(theme) {
  return {
    width: '100%',
    textAlign: 'left',
    border: `1px solid ${theme.borderSoft}`,
    borderRadius: 12,
    background: theme.taskSurface || theme.surfaceSoft,
    padding: 14,
    cursor: 'pointer',
    boxShadow: theme.shadow,
  };
}

function labelStyle(theme) {
  return {
    display: 'block',
    color: theme.muted,
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 14,
  };
}

function inputStyle(theme) {
  return {
    width: '100%',
    marginTop: 6,
    padding: '10px 12px',
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    outline: 'none',
    color: theme.text,
    background: theme.surfaceSoft,
    fontSize: 14,
    fontWeight: 600,
    boxSizing: 'border-box',
  };
}

function errorStyle(theme) {
  return {
    color: theme.danger,
    background: theme.dangerSoft,
    border: `1px solid ${theme.danger}`,
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 13,
    marginBottom: 10,
  };
}

function messageStyle(theme) {
  return {
    color: theme.success,
    background: theme.successSoft,
    border: `1px solid ${theme.success}`,
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 13,
    marginBottom: 10,
  };
}
